import { registerLocaleData } from "@angular/common";
import { provideHttpClient, withInterceptors } from "@angular/common/http";
import localeDe from '@angular/common/locales/de';
import localeDeCH from '@angular/common/locales/de-CH';
import localeEs from '@angular/common/locales/es';
import localeFi from '@angular/common/locales/fi';
import localeFr from '@angular/common/locales/fr';
import localeFrCH from '@angular/common/locales/fr-CH';
import localeIt from '@angular/common/locales/it';
import localeItCh from '@angular/common/locales/it-CH';
import localeNl from '@angular/common/locales/nl';
import localePl from '@angular/common/locales/pl';
import localeSv from '@angular/common/locales/sv';
import { APP_INITIALIZER, ApplicationConfig, ErrorHandler, importProvidersFrom, LOCALE_ID, provideZoneChangeDetection } from "@angular/core";
import { REMOVE_STYLES_ON_COMPONENT_DESTROY } from "@angular/platform-browser";
import { provideAnimations } from "@angular/platform-browser/animations";
import { StorageModule } from "@ngx-pwa/local-storage";
import { provideTranslateLoader, provideTranslateService, TranslateCompiler, TranslateLoader } from "@ngx-translate/core";
import { BrandTranslateCompiler } from "./shared/compilers/brand-translate-compiler";
import { createErrorHandler, TraceService } from "@sentry/angular";
import { DateAdapter, provideCalendar } from "angular-calendar";
import { adapterFactory } from "angular-calendar/date-adapters/date-fns";
import { provideNgxStripe } from "ngx-stripe";
import { Observable, of } from "rxjs";
import { de_CH } from "../assets/i18n/de_CH";
import { en_US } from "../assets/i18n/en_US";
import { es_ES } from "../assets/i18n/es_ES";
import { fi_FI } from "../assets/i18n/fi_FI";
import { fr_CH } from "../assets/i18n/fr_CH";
import { it_CH } from "../assets/i18n/it_CH";
import { nl_NL } from "../assets/i18n/nl_NL";
import { pl_PL } from "../assets/i18n/pl_PL";
import { sv_SE } from "../assets/i18n/sv_SE";
import { environment } from "../environments/environment";
import { AuthTokenInterceptor } from "./interceptors/auth-token.interceptor";
import { AuthInterceptor } from "./interceptors/auth.interceptor";
import { GlobalErrorHandler } from "./interceptors/global-error-handler.interceptor";

registerLocaleData(localeDe, 'de');
registerLocaleData(localeDeCH, 'ch');
registerLocaleData(localeFr, 'fr');
registerLocaleData(localeFrCH, 'fr_CH');
registerLocaleData(localeIt, 'it');
registerLocaleData(localeItCh, 'it_CH');
registerLocaleData(localeNl, 'nl');
registerLocaleData(localePl, 'pl');
registerLocaleData(localeFi, 'fi');
registerLocaleData(localeEs, 'es');
registerLocaleData(localeSv, 'sv');

const TRANSLATION_MAP: Record<string, any> = {
  de_CH, en_US, fi_FI, fr_CH, it_CH, nl_NL, pl_PL, es_ES, sv_SE,
};

export class CustomTranslateLoader implements TranslateLoader {
  getTranslation(lang: string): Observable<any> {
    return of(TRANSLATION_MAP[lang] || TRANSLATION_MAP[environment.defaultLocale]);
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    // Angular 21 defaults to zoneless. This app renders off plain fields mutated in
    // setTimeout/RxJS callbacks rather than signals, so without zone.js nothing ever
    // repaints after the first render.
    provideZoneChangeDetection(),
    importProvidersFrom(
      StorageModule.forRoot({ IDBNoWrap: false }),
    ),
    provideTranslateService({
      loader: provideTranslateLoader(CustomTranslateLoader),
      compiler: { provide: TranslateCompiler, useClass: BrandTranslateCompiler },
    }),
    provideCalendar({ provide: DateAdapter, useFactory: adapterFactory }),
    provideAnimations(),
    provideHttpClient(withInterceptors([AuthTokenInterceptor, AuthInterceptor])),
    provideNgxStripe(environment.stripe.publishableKey),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    { provide: LOCALE_ID, useValue: environment.defaultLocale.replace('_', '-').toLowerCase() },
    { provide: ErrorHandler, useValue: createErrorHandler({ showDialog: false }) },
    { provide: APP_INITIALIZER, useFactory: () => () => { }, deps: [TraceService], multi: true },
    { provide: REMOVE_STYLES_ON_COMPONENT_DESTROY, useValue: false },
  ],
}